import type {
  ChatMessageData,
  ChatResponse,
  CompletionResponse,
  ContactDetails,
  InquiryAnswer,
} from "./types";
import type { SiteConfig } from "./site-config";
import { publicApiBase } from "./public-path";

const ACTIVE_MODEL_ID = "web-production";

function publicApiPath(path: string) {
  return `${publicApiBase()}${path}`;
}

export async function getAppMode() {
  const response = await fetch(publicApiPath("/health"));
  if (!response.ok) return "live" as const;
  const data = (await response.json()) as { mode?: string };
  return data.mode === "demo" ? "demo" as const : "live" as const;
}

export async function getSiteConfig() {
  return postOrGetJson<SiteConfig>(publicApiPath("/config"));
}

export async function startAnalyticsSession() {
  return postJson<{ sessionId: string | null; analyticsEnabled: boolean }>(
    publicApiPath("/analytics/session"),
    {},
  );
}

export function sendAnalyticsLeave(sessionId: string) {
  const body = new Blob([JSON.stringify({ sessionId })], {
    type: "application/json",
  });
  return navigator.sendBeacon(publicApiPath("/analytics/leave"), body);
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const data = (await response.json().catch(() => null)) as
    | (T & { error?: string })
    | null;

  if (!response.ok) {
    throw new Error(data?.error ?? "通信に失敗しました。時間をおいてもう一度お試しください。");
  }

  return data as T;
}

export function requestNextQuestion(
  messages: ChatMessageData[],
  answers: InquiryAnswer[],
  sessionId?: string | null,
) {
  return postJson<ChatResponse>(publicApiPath("/chat"), {
    modelId: ACTIVE_MODEL_ID,
    ...(sessionId ? { sessionId } : {}),
    messages: messages.map(({ role, content }) => ({ role, content })),
    answers,
  });
}

export function completeInquiry(
  messages: ChatMessageData[],
  answers: InquiryAnswer[],
  contact: ContactDetails,
  sessionId?: string | null,
) {
  return postJson<CompletionResponse>(publicApiPath("/complete"), {
    modelId: ACTIVE_MODEL_ID,
    ...(sessionId ? { sessionId } : {}),
    messages: messages.map(({ role, content }) => ({ role, content })),
    answers,
    contact,
  });
}

async function postOrGetJson<T>(path: string): Promise<T> {
  const response = await fetch(path, { cache: "no-store" });
  const data = await response.json().catch(() => null) as (T & { error?: string }) | null;
  if (!response.ok) {
    throw new Error(data?.error ?? "会社情報を取得できませんでした。");
  }
  return data as T;
}
